<template>
    <v-menu ref="menu" v-model="project_menu" :close-on-content-click="false" transition="scale-transition" offset-y
        min-width="290px">
        <template v-slot:activator="{ props }">
            <!-- No loading state of its own: the app-wide bar in App.vue reports
                 it, and it does so in rail mode and on a phone, where this card
                 is not on screen at all. -->
            <v-card variant="flat" class="border-thin w-100" v-bind="props">
                <div class="d-flex text-grey" :title="selected_project_title">
                    <v-icon v-if="stdlib_mode" class="ml-2 mr-1 my-3">
                        mdi-database
                    </v-icon>
                    <v-icon class="ml-2 mr-1 my-3" v-else>
                        mdi-home-modern
                    </v-icon>
                    <!-- Two lines when the project number is shown: joining both in one
                         line would truncate the name away in this narrow card. The card
                         keeps its height because the icon (24px + my-3) is taller than
                         these two lines (16px + 20px), so nothing grows. -->
                    <div v-if="selected_number" class="d-flex flex-column justify-center" style="min-width: 0">
                        <span class="text-caption text-truncate" style="line-height: 1rem">
                            {{ selected_number }}
                        </span>
                        <span class="text-body-2 text-truncate">
                            <strong v-if="stdlib_mode">stdlib:</strong>
                            {{ selected_project_name }}
                        </span>
                    </div>
                    <span v-else class="my-3 d-inline-block text-truncate" style="min-width: 0">
                        <strong v-if="stdlib_mode">stdlib:</strong>
                        {{ selected_project_name }}
                    </span>
                </div>
            </v-card>
        </template>
        <v-list density="compact" class="overflow-y-auto" style="max-height: 80vh" v-model:opened="opened_groups">
            <v-list-item v-if="show_project_number">
                <v-text-field :label="t('projects_filter')" variant="outlined" density="compact" hide-details
                    clearable v-model="search" prepend-inner-icon="mdi-magnify"></v-text-field>
            </v-list-item>
            <v-list-item prepend-icon="mdi-factory" :title="t('company_wide')" @click="selectCompanyWideMenu"
                v-if="manager_or_admin">
            </v-list-item>
            <v-divider v-if="manager_or_admin"></v-divider>
            <v-list-item prepend-icon="mdi-home-modern" :title="t('no_projects')" v-if="visible_projects.length == 0">
            </v-list-item>
            <template v-for="(item, index) in visible_projects">
                <v-list-group v-if="item.is_super" :key="index" :value="item.name">
                    <template v-slot:activator="{ props }">
                        <v-list-item v-bind="props" prepend-icon="mdi-city-variant-outline" :title="item.name"
                            :subtitle="item.number || undefined"></v-list-item>
                    </template>
                    <v-list-item prepend-icon="mdi-home-modern" :title="pr.name" :subtitle="pr.number || undefined"
                        @click="selectProject(pr.uuid)" v-for="pr in item.projects" :key="pr.uuid"> </v-list-item>
                </v-list-group>
                <v-list-item prepend-icon="mdi-home-modern" :title="item.name" :subtitle="item.number || undefined"
                    @click="selectProject(item.uuid)" v-else :key="index + '_else'"> </v-list-item>
            </template>
            <v-list-group v-if="menu_stdlibs.length != 0" value="stdlibs" prepend-icon="mdi-database">
                <template v-slot:activator="{ props }">
                    <v-list-item v-bind="props" prepend-icon="mdi-city-variant-outline"
                        :title='t("standard_libraries")'></v-list-item>
                </template>
                <v-list-item prepend-icon="mdi-home-modern" :title="stdlib.name" @click="selectSTDLIB(stdlib.uuid)"
                    v-for="stdlib in menu_stdlibs" :key="stdlib.uuid">
                </v-list-item>
            </v-list-group>
        </v-list>
    </v-menu>
</template>

<script>
import {
    COMPANY_TO_PR_ROUTE,
    PR_TO_COMPANY_ROUTE,
    MISC_ROUTES,
    VIEWER_ROUTES,
    NO_REDIRECT_ROUTES,
} from "@/router/redirections";
import { useFullPitStore } from "@/stores/app_common";
import { nameSort } from "/src/utils/basics.js";
import { captureException } from "@sentry/vue";

export default {
    setup() {
        const pit_store = useFullPitStore()
        return { ...pit_store, }
    },
    data: () => ({ project_menu: false, selected_project: "all", search: "", opened_groups: [] }),
    computed: {
        show_project_number() {
            return this.company_store.show_project_number;
        },
        projects() {
            return this.company_store.projects;
        },
        standard_libraries() {
            return this.company_store.standard_libraries;
        },
        projects_and_stdlibs() {
            return this.projects.concat(this.standard_libraries)
        },
        stdlib_mode() {
            return this.selected_store.stdlib_mode
        },
        manager_or_admin() {
            return !this.session_store.iam_prod_worker;
        },
        company_wide() {
            return this.selected_project == "all";
        },
        selected_project_name() {
            if (this.company_wide) return this.t("company_wide");
            return (
                this.projects_and_stdlibs.find((p) => p.uuid == this.selected_project)
                    ?.name || this.t("project")
            );
        },
        selected_number() {
            if (this.company_wide) return "";
            const pr = this.projects_and_stdlibs.find(
                (p) => p.uuid == this.selected_project
            );
            const label = this.company_store.project_label(pr);
            return label.show_number ? label.number : "";
        },
        selected_project_title() {
            if (!this.selected_number) return this.selected_project_name;
            return `${this.selected_number} — ${this.selected_project_name}`;
        },
        menu_projects() {
            let items = [];
            this.projects.forEach((pr) => {
                const label = this.company_store.project_label(pr);
                const number = label.show_number ? label.number : "";
                if (pr.super_project != "") {
                    let idx = items.findIndex(
                        (it) => it.name == pr.super_project
                    );
                    if (idx != -1) {
                        (items[idx].projects ??= []).push({
                            uuid: pr.uuid,
                            name: pr.name,
                            number,
                        });
                    } else {
                        items.push({
                            name: pr.super_project,
                            is_super: true,
                            number: "",
                            projects: [
                                {
                                    uuid: pr.uuid,
                                    name: pr.name,
                                    number,
                                },
                            ],
                        });
                    }
                } else {
                    items.push({
                        uuid: pr.uuid,
                        name: pr.name,
                        number,
                        is_super: false,
                    });
                }
            });
            // Sort list
            items.sort((a, b) => nameSort(a.name, b.name))
            // Sort nested
            items.forEach((item) => {
                if (item.is_super)
                    item.projects.sort((a, b) => nameSort(a.name, b.name))
            });
            // Most companies reuse one order number for every project of a super
            // project: show it once on the group header instead of on each child.
            items.forEach((item) => {
                if (!item.is_super) return
                const numbers = [...new Set(item.projects.map((pr) => pr.number))]
                if (numbers.length == 1 && numbers[0]) {
                    item.number = numbers[0]
                    item.projects.forEach((pr) => (pr.number = ""))
                }
            });
            return items;
        },
        visible_projects() {
            const s = (this.search || "").trim().toLowerCase()
            if (!s) return this.menu_projects
            const hit = (text) => (text || "").toLowerCase().includes(s)
            return this.menu_projects
                .map((item) => {
                    if (!item.is_super) return hit(item.name) || hit(item.number) ? item : null
                    // A matching super project keeps all its children
                    if (hit(item.name) || hit(item.number)) return item
                    const projects = item.projects.filter((pr) => hit(pr.name) || hit(pr.number))
                    return projects.length ? { ...item, projects } : null
                })
                .filter(Boolean)
        },
        menu_stdlibs() {

            let items = this.standard_libraries.map((stdlib) => ({
                uuid: stdlib.uuid,
                name: stdlib.name,
            }))
            // Sort list
            items.sort((a, b) => nameSort(a.name, b.name))
            return items;
        },
        store_pr_selection() {
            return this.selected_store.pr_selection
        }
    },
    watch: {
        project_menu(open) {
            if (!open) this.search = ""
        },
        search(s) {
            // Matches inside a collapsed group would stay hidden otherwise
            this.opened_groups = s
                ? this.visible_projects.filter((it) => it.is_super).map((it) => it.name)
                : []
        },
        store_pr_selection: {
            immediate: true,  // True so when uncollapse it loads correct pr.
            handler: function (pr_uuid) {
                this.selected_project = pr_uuid
            }
        },
        selected_project(sp, sp_old) {
            // No change (all to all, or pr to same pr)
            if (!sp || sp == sp_old) return;
            if (NO_REDIRECT_ROUTES.includes(this.$route.name)) {

                // Project needs to be loaded
                if (sp == "all") {
                    this.selected_store.setCompanyWide()
                    this.selected_store.setProjectSelection("all")
                } else {
                    this.selected_store.loadProject(sp)
                }
                return;
            }

            // PUSH ROUTES

            // From project to company
            if (sp == "all") {
                this.selected_store.setCompanyWide()
                this.selected_store.setProjectSelection("all")
                const rn =
                    PR_TO_COMPANY_ROUTE?.[this.$route.name] ||
                    "company_settings";
                this.$router.push({ name: rn }).catch((e) => {
                    console.log(e)
                    captureException(e)
                });
                return;
            }
            // From company to project
            if (sp_old == "all") {
                const rn = COMPANY_TO_PR_ROUTE?.[this.$route.name] || "designs";
                const params = {
                    ...this.$route.params,
                    project_uuid: sp,
                };
                this.$router
                    .push({ name: rn, params })
                    .catch((e) => {
                        console.log(e)
                        captureException(e)
                    });
                return;  // project will be loaded on route guard
            }

            // From project to project
            const params = {
                ...this.$route.params,
                ...(sp && { project_uuid: sp }), // conditionally add
            };

            // project will be loaded on route guard
            if (VIEWER_ROUTES.includes(this.$route.name) || MISC_ROUTES.includes(this.$route.name))
                this.$router
                    .push({ name: "project_viewer", params })
                    .catch((e) => {
                        console.log(e)
                        captureException(e)
                    });
            else
                this.$router
                    .push({ name: this.$route.name, params })
                    .catch((e) => {
                        console.log(e)
                        captureException(e)
                    });
        },
    },
    methods: {
        selectProject(pr_uuid) {
            this.selected_project = pr_uuid;
            this.project_menu = false;
            this.selected_store.setProjectMode()
        },
        selectSTDLIB(stdlib_uuid) {
            this.selected_project = stdlib_uuid;
            this.project_menu = false;
            this.selected_store.setSTDLIBMode()
        },
        selectCompanyWideMenu() {
            this.selected_project = "all";
            this.project_menu = false;
            this.selected_store.setProjectMode()
            this.app_store.setManagerMode()
        },
    },
};
</script>